import { useEffect, useState } from 'react'
import { kycRepository } from '../../data/repositories/KycRepositoryImpl'

interface KycSensitiveDetail {
  name: string
  phone: string
  email: string
  address: string
  pan: string
  gstin: string
  panDocUrl: string | null
  gstinDocUrl: string | null
}

interface UseKycSensitiveDetailParams {
  kycId: string | null
  hasPanDoc: boolean
  hasGstinDoc: boolean
}

// Fetches the decrypted PAN/GSTIN (GET /kyc/requests/:id/sensitive) plus the PAN and GST
// document preview URLs (GET /kyc/requests/:id/document?type=) whenever a KYC review modal
// opens. Every one of these calls requires approve_kyc and is audit-logged server-side.
export const useKycSensitiveDetail = ({ kycId, hasPanDoc, hasGstinDoc }: UseKycSensitiveDetailParams) => {
  const [data, setData] = useState<KycSensitiveDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!kycId) {
      setData(null)
      setError(null)
      return undefined
    }

    let cancelled = false

    const fetchDetail = async () => {
      setLoading(true)
      setError(null)
      try {
        const [sensitive, panDocUrl, gstinDocUrl] = await Promise.all([
          kycRepository.getSensitiveDetails(kycId),
          hasPanDoc ? kycRepository.getDocumentUrl(kycId, 'pan_card') : Promise.resolve(null),
          hasGstinDoc ? kycRepository.getDocumentUrl(kycId, 'gstin') : Promise.resolve(null),
        ])

        if (!cancelled) {
          setData({
            name: sensitive.name,
            phone: sensitive.phone,
            email: sensitive.email,
            address: sensitive.address,
            pan: sensitive.pan,
            gstin: sensitive.gstin,
            panDocUrl,
            gstinDocUrl,
          })
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const errorVal = err as Error
          setError(errorVal.message || 'Failed to load KYC details.')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchDetail()

    return () => {
      cancelled = true
    }
  }, [kycId, hasPanDoc, hasGstinDoc])

  return { data, loading, error }
}
